import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface PrescriptionItem {
  id: string;
  brandName: string;
  activePrinciple: string;
  form: string;
  dosage: string;
  quantity: number;
  instructions?: string;
}

interface PrintablePrescriptionProps {
  prescriptionNumber: string;
  doctor: {
    firstName: string;
    lastName: string;
    licenseNumber: string;
    clinicName?: string;
    rncTaxId?: string;
    clinicAddress?: string;
    cmdNumber?: string;
    specialty?: { name: string };
  };
  patient: {
    name: string;
    dateOfBirth?: Date | string;
  };
  items: PrescriptionItem[];
  orderDate: Date | string;
}

const calculateAge = (dob: Date | string): number | null => {
  if (!dob) return null;
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

export const PrintablePrescription: React.FC<PrintablePrescriptionProps> = ({
  prescriptionNumber,
  doctor,
  patient,
  items,
  orderDate,
}) => {
  const { t, i18n } = useTranslation();
  const age = patient.dateOfBirth ? calculateAge(patient.dateOfBirth) : null;

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString(
      i18n.language === 'es' ? 'es-DO' : 'en-US',
      { year: 'numeric', month: 'long', day: 'numeric' }
    );
  };

  return (
    <Box
      className="printable-prescription"
      sx={{
        fontFamily: 'serif',
        color: '#000',
        backgroundColor: '#fff',
        padding: '20px',
        maxWidth: '800px',
        margin: '0 auto',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          textAlign: 'center',
          borderBottom: '2px solid #000',
          paddingBottom: '16px',
          marginBottom: '24px',
        }}
      >
        {doctor.clinicName && (
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '8px' }}
          >
            {doctor.clinicName}
          </Typography>
        )}
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Dr. {doctor.firstName} {doctor.lastName}
        </Typography>
        {doctor.specialty && (
          <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
            {doctor.specialty.name}
          </Typography>
        )}
        {doctor.rncTaxId && (
          <Typography variant="body2">RNC: {doctor.rncTaxId}</Typography>
        )}
        {doctor.clinicAddress && (
          <Typography variant="body2">{doctor.clinicAddress}</Typography>
        )}
      </Box>

      {/* Rx Section */}
      <Box sx={{ minHeight: '350px', paddingBottom: '24px' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '24px',
          }}
        >
          <Typography
            variant="h3"
            sx={{ fontWeight: 'bold', fontFamily: 'serif', fontStyle: 'italic' }}
          >
            Rx
          </Typography>
          <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
            #{prescriptionNumber}
          </Typography>
        </Box>

        {/* Medications */}
        {items.map((item) => (
          <Box key={item.id} sx={{ marginBottom: '20px', paddingLeft: '24px' }}>
            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '4px' }}
            >
              {item.brandName} {item.form}
            </Typography>
            <Typography variant="body1" sx={{ paddingLeft: '16px' }}>
              USO: {item.dosage}
            </Typography>
            {item.instructions && (
              <Typography variant="body2" sx={{ paddingLeft: '16px', fontStyle: 'italic' }}>
                {item.instructions}
              </Typography>
            )}
            <Typography variant="body2" sx={{ paddingLeft: '16px' }}>
              {t('common.quantity')}: {item.quantity}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Signature Area */}
      <Box sx={{ textAlign: 'right', marginTop: '32px', marginBottom: '24px' }}>
        <Box
          sx={{
            display: 'inline-block',
            borderTop: '1px solid #000',
            paddingTop: '8px',
            minWidth: '250px',
            textAlign: 'center',
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            Dr. {doctor.firstName} {doctor.lastName}
          </Typography>
          {doctor.specialty && (
            <Typography variant="body2">{doctor.specialty.name}</Typography>
          )}
          <Typography variant="body2">Exeq: {doctor.licenseNumber}</Typography>
          {doctor.cmdNumber && (
            <Typography variant="body2">CMD: {doctor.cmdNumber}</Typography>
          )}
        </Box>
      </Box>

      <Divider sx={{ borderColor: '#000', marginY: '16px' }} />

      {/* Patient Footer */}
      <Box sx={{ paddingTop: '16px' }}>
        <Typography variant="body1" sx={{ marginBottom: '4px' }}>
          <strong>{t('prescription.patient')}:</strong> {patient.name}
        </Typography>
        {age !== null && (
          <Typography variant="body1" sx={{ marginBottom: '4px' }}>
            <strong>{t('prescription.age')}:</strong> {age} {t('prescription.years')}
          </Typography>
        )}
        <Typography variant="body1">
          <strong>{t('prescription.date')}:</strong> {formatDate(orderDate)}
        </Typography>
      </Box>
    </Box>
  );
};

export default PrintablePrescription;
